import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, FileDown, TrendingUp, TrendingDown } from 'lucide-react';
import { useForecastData } from '../hooks/useForecast';
import { formatFiscalYear } from '../utils/forecastEngine';
import { supabase } from '../lib/supabase';
import ForecastExportModal from '../components/forecast/ForecastExportModal';
import { exportForecastToPDF, exportForecastToExcel } from '../utils/forecastExport';

export default function ForecastDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const forecastData = useForecastData(id || null);
  const { scenario, lineItems, calculation, loading, loadAll } = forecastData;
  const [editingCell, setEditingCell] = useState<{ lineId: string; yearIndex: number } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const startEdit = (lineId: string, yearIndex: number, currentValue: number) => {
    setEditingCell({ lineId, yearIndex });
    setEditValue(currentValue > 0 ? String(currentValue) : '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell || !scenario) return;

    const value = parseFloat(editValue) || 0;
    setSaving(true);

    try {
      const { error } = await supabase.from('forecast_overrides').upsert({
        line_item_id: editingCell.lineId,
        year_index: editingCell.yearIndex,
        override_amount: value
      }, {
        onConflict: 'line_item_id,year_index'
      });

      if (error) throw error;

      await loadAll();
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error saving override:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-gray-500">Chargement...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!scenario || !calculation) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-red-600">Scénario non trouvé</div>
          </div>
        </div>
      </div>
    );
  }

  const startDate = new Date(scenario.start_date);
  const yearLabels = Array.from({ length: scenario.years_count }, (_, i) =>
    formatFiscalYear(startDate, i + 1)
  );

  const getLineAmount = (lineId: string, yearIndex: number): number => {
    const yearAmount = calculation.yearly_amounts.find(
      ya => ya.line_item_id === lineId && ya.year_index === yearIndex
    );
    return yearAmount ? yearAmount.amount : 0;
  };

  const isEditing = (lineId: string, yearIndex: number): boolean => {
    return editingCell?.lineId === lineId && editingCell?.yearIndex === yearIndex;
  };

  const renderEditableCell = (lineId: string, yearIndex: number, amount: number) => {
    const editing = isEditing(lineId, yearIndex);

    if (editing) {
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={saveEdit}
          autoFocus
          disabled={saving}
          className="w-full px-2 py-1 text-right text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }

    return (
      <button
        onClick={() => startEdit(lineId, yearIndex, amount)}
        className="w-full text-right px-2 py-1 hover:bg-blue-50 rounded transition-colors cursor-pointer"
      >
        {amount > 0
          ? amount.toLocaleString('fr-FR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })
          : '-'}
      </button>
    );
  };

  // Organiser les lignes par section
  const expensesBySection = new Map<string, typeof lineItems>();
  const revenuesBySection = new Map<string, typeof lineItems>();

  lineItems.forEach(line => {
    if (line.type === 'expense') {
      if (!expensesBySection.has(line.section)) {
        expensesBySection.set(line.section, []);
      }
      expensesBySection.get(line.section)!.push(line);
    } else {
      if (!revenuesBySection.has(line.section)) {
        revenuesBySection.set(line.section, []);
      }
      revenuesBySection.get(line.section)!.push(line);
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app/forecast')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{scenario.name}</h1>
                <p className="text-sm text-gray-600">Compte d'exploitation prévisionnel</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/app/forecast/${id}/edit`)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit size={18} />
                Éditer
              </button>
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileDown size={18} />
                Exporter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {calculation.totals_by_year.map(total => (
            <div key={total.year_index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600 mb-1">Année {total.year_index}</div>
              <div className="flex items-center gap-2">
                {total.net_result >= 0 ? (
                  <TrendingUp className="text-green-600" size={20} />
                ) : (
                  <TrendingDown className="text-red-600" size={20} />
                )}
                <div
                  className={`text-lg font-bold ${
                    total.net_result >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {total.net_result.toLocaleString('fr-FR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })}{' '}
                  {scenario.currency}
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">Résultat net</div>
            </div>
          ))}
        </div>

        {/* Tableau compte d'exploitation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: `${400 + scenario.years_count * 150}px` }}>
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 w-64">
                    Libellé
                  </th>
                  {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(yearIndex => (
                    <th
                      key={yearIndex}
                      className="px-4 py-3 text-right text-xs font-medium text-gray-600"
                    >
                      Année {yearIndex}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Produits */}
                <tr className="bg-green-50">
                  <td colSpan={scenario.years_count + 1} className="px-4 py-2 font-semibold text-green-800">
                    PRODUITS
                  </td>
                </tr>
                {Array.from(revenuesBySection.entries()).map(([section, lines]) => (
                  <>
                    <tr key={`revenue-section-${section}`} className="bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">{section}</td>
                      {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                        yearIndex => {
                          const sectionTotal = lines.reduce(
                            (sum, line) => sum + getLineAmount(line.id, yearIndex),
                            0
                          );
                          return (
                            <td key={yearIndex} className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                              {sectionTotal.toLocaleString('fr-FR', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              })}
                            </td>
                          );
                        }
                      )}
                    </tr>
                    {lines.map(line => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-600 pl-8">{line.label}</td>
                        {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                          yearIndex => {
                            const amount = getLineAmount(line.id, yearIndex);
                            return (
                              <td key={yearIndex} className="px-4 py-2 text-sm text-gray-900">
                                {renderEditableCell(line.id, yearIndex, amount)}
                              </td>
                            );
                          }
                        )}
                      </tr>
                    ))}
                  </>
                ))}
                <tr className="bg-green-100 border-t-2 border-green-200">
                  <td className="px-4 py-3 text-sm font-bold text-green-900">TOTAL PRODUITS</td>
                  {calculation.totals_by_year.map(total => (
                    <td
                      key={total.year_index}
                      className="px-4 py-3 text-right text-sm font-bold text-green-900"
                    >
                      {total.total_revenues.toLocaleString('fr-FR', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </td>
                  ))}
                </tr>

                {/* Charges */}
                <tr className="bg-red-50">
                  <td colSpan={scenario.years_count + 1} className="px-4 py-2 font-semibold text-red-800">
                    CHARGES
                  </td>
                </tr>
                {Array.from(expensesBySection.entries()).map(([section, lines]) => (
                  <>
                    <tr key={`expense-section-${section}`} className="bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">{section}</td>
                      {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                        yearIndex => {
                          const sectionTotal = lines.reduce(
                            (sum, line) => sum + getLineAmount(line.id, yearIndex),
                            0
                          );
                          return (
                            <td key={yearIndex} className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                              {sectionTotal.toLocaleString('fr-FR', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              })}
                            </td>
                          );
                        }
                      )}
                    </tr>
                    {lines.map(line => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-600 pl-8">{line.label}</td>
                        {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                          yearIndex => {
                            const amount = getLineAmount(line.id, yearIndex);
                            return (
                              <td key={yearIndex} className="px-4 py-2 text-sm text-gray-900">
                                {renderEditableCell(line.id, yearIndex, amount)}
                              </td>
                            );
                          }
                        )}
                      </tr>
                    ))}
                  </>
                ))}
                <tr className="bg-red-100 border-t-2 border-red-200">
                  <td className="px-4 py-3 text-sm font-bold text-red-900">TOTAL CHARGES</td>
                  {calculation.totals_by_year.map(total => (
                    <td
                      key={total.year_index}
                      className="px-4 py-3 text-right text-sm font-bold text-red-900"
                    >
                      {total.total_expenses.toLocaleString('fr-FR', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </td>
                  ))}
                </tr>

                {/* Résultat */}
                <tr className="bg-blue-50 border-t-4 border-blue-300">
                  <td className="px-4 py-4 text-base font-bold text-blue-900">RÉSULTAT NET</td>
                  {calculation.totals_by_year.map(total => (
                    <td
                      key={total.year_index}
                      className={`px-4 py-4 text-right text-base font-bold ${
                        total.net_result >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}
                    >
                      {total.net_result.toLocaleString('fr-FR', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ForecastExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportPDF={() => exportForecastToPDF(scenario, lineItems, calculation)}
        onExportExcel={() => exportForecastToExcel(scenario, lineItems, calculation)}
        scenarioName={scenario.name}
      />
    </div>
  );
}
